import { useCallback } from 'react';

import { DEFAULT_PRO_SUBSCRIBE_OFFER_CODE } from '@components/pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import { SUBSCRIBE_NOW, UPGRADE_TO_PRO } from '@components/pages/OrgCharts/constants';
import { Offers } from '@contexts/AppContext';
import { subscribe_path, upgrade_subscription_path } from '@routes';
import { ClickTarget, EventProperties, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';

interface UsePaywallCTAsProps {
  currentUser: User;
  offers?: Offers;
  subscribeText?: string;
  trackEvent: (event: EventProperties) => void;
  upgradeText?: string;
}

interface UsePaywallCTAsReturnValues {
  handleCTAClickTracking: () => void;
  paywallCTAPath: () => string | undefined;
  paywallCTAText: string;
}

const usePaywallCTAs = ({
  currentUser,
  offers,
  subscribeText = SUBSCRIBE_NOW,
  upgradeText = UPGRADE_TO_PRO,
  trackEvent,
}: UsePaywallCTAsProps): UsePaywallCTAsReturnValues => {
  const isSubscriber: boolean = currentUser?.is_subscriber;
  const isLead: boolean = currentUser && !isSubscriber;
  const isCBPT: boolean = currentUser?.subscription?.is_cbpt;
  const showUpgradeCTA: boolean = isSubscriber && !isCBPT;
  const showKnownUserSubscribeCTA: boolean = isLead || isCBPT;
  const paywallCTAText: string = showUpgradeCTA
    ? upgradeText
    : showKnownUserSubscribeCTA
    ? subscribeText
    : '';

  const paywallCTAPath = useCallback(() => {
    if (showUpgradeCTA) {
      return upgrade_subscription_path({ offer: offers?.upgrade ?? undefined });
    } else if (showKnownUserSubscribeCTA) {
      const offerCode = offers?.subscribe || DEFAULT_PRO_SUBSCRIBE_OFFER_CODE;
      return subscribe_path({ offer: offerCode });
    }
  }, [showUpgradeCTA, showKnownUserSubscribeCTA, offers]);

  const handleCTAClickTracking = useCallback(() => {
    if (showUpgradeCTA) {
      trackEvent(click({ clickTarget: ClickTarget.Upgrade, clickText: upgradeText }));
    } else if (showKnownUserSubscribeCTA) {
      trackEvent(click({ clickTarget: ClickTarget.Subscribe, clickText: subscribeText }));
    }
  }, [showUpgradeCTA, showKnownUserSubscribeCTA, subscribeText, trackEvent, upgradeText]);

  return {
    handleCTAClickTracking,
    paywallCTAPath,
    paywallCTAText,
  };
};

export default usePaywallCTAs;
